import {
  FocalEventPosterImage,
} from '@/features/events/shared/FocalEventPosterImage';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/async-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AppTopBar } from '@/components/ui/AppTopBar';
import { VenueDealCard } from '@/features/venues/deals/VenueDealCard';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { AppScreen, Card, IconAction, MetaBadge } from '@/components/ui/scene';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { UnavailableState } from '@/components/ui/UnavailableState';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useAuth } from '@/lib/auth/auth';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { APP_CONFIG } from '@/lib/app/config';
import type {
  VenueHour,
  VenueOwnerEventSummary,
  VenuePublicDeal,
  VenueSocialLink,
} from '@/lib/api/apiTypes';
import { openAddressInMaps, openDirections } from '@/lib/location/directions';
import { getDealTiming, sortDealsByTiming } from '@/features/venues/deals/deals';
import { formatEventAgeRestriction, getEventTagLabel } from '@/features/events/eventTags';
import {
  trackDealDirectionsTapped,
  trackDealSaved,
  trackDealShare,
  trackVenueBookingTapped,
  trackVenueClaimStarted,
  trackVenueDirectionsTapped,
  trackVenueFollow,
  trackVenueInstagramTapped,
  trackVenueProfileView,
  trackVenueShare,
  trackVenueSocialLinkTapped,
  trackVenueUnfollow,
  trackVenueWebsiteTapped,
} from '@/lib/analytics/posthog';
import { type DiscoveryEvent } from '@/lib/api/apiTypes';
import { formatEventDayLabel, formatEventTime } from '@/features/events/eventFormatting';
import { buildVenueLink, getEventHref, getRouteEntityID, getVenueHref } from '@/lib/navigation/routes';
import { shareLink } from '@/lib/platform/share';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { getVenueBackstageAction } from '@/features/venues/venueBackstage';
import { getVenueCategoryConfig, resolveVenueCategory } from '@/features/venues/venueCategories';
import { useDeleteEvent } from '@/features/events/useDeleteEvent';
import { useVenueDetail } from '@/features/venues/useVenueDetail';
import { VenueActivitySpotlight } from '@/features/venues/VenueActivitySpotlight';
import { VenueDealsPreview } from '@/features/venues/deals/VenueDealsPreview';
import { VenueUpcomingPreview } from '@/features/venues/VenueUpcomingPreview';
import { useVenueOwnerOverview } from '@/features/venues/useVenueOwnerOverview';
import { useVenueEvents } from '@/features/venues/useVenueEvents';
import { useVenueFollow } from '@/features/venues/useVenueFollow';
import { useSavedVenueDeal } from '@/features/venues/deals/useSavedVenueDeal';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import {
  AtSign,
  ArrowLeftIcon,
  ArrowRightIcon,
  BadgeCheckIcon,
  CheckIcon,
  Clock3,
  Disc3,
  EyeIcon,
  ExternalLink,
  Facebook,
  Globe,
  Instagram,
  Link,
  MapPinIcon,
  Music2,
  PencilIcon,
  Phone,
  PlusIcon,
  ShareIcon,
  StoreIcon,
  Trash2Icon,
  TreePalm,
  UsersIcon,
  Youtube,
} from 'lucide-react-native';
import * as React from 'react';
import { Linking, Platform, Pressable, ScrollView, View } from 'react-native';

type VenuePageSource = 'profile' | 'event_detail' | 'search' | 'map_sheet';

const VALID_SOURCES = new Set<VenuePageSource>(['profile', 'event_detail', 'search', 'map_sheet']);

function resolveSource(value?: string | string[]): VenuePageSource {
  if (typeof value === 'string' && VALID_SOURCES.has(value as VenuePageSource)) {
    return value as VenuePageSource;
  }
  return 'search';
}

export default function VenuePageScreen() {
  const params = useLocalSearchParams<{ deal?: string; id: string; source?: string }>();
  const session = useAuth((state) => state.session);
  const { membershipsQuery } = useAccountContext();
  const isDesktop = useIsDesktop();
  const venueID = getRouteEntityID(params.id);
  const loadingVenueName = getVenueLoadingName(params.id);
  const source = resolveSource(params.source);
  const highlightedDealID = typeof params.deal === 'string' ? params.deal : null;
  const venueDetailQuery = useVenueDetail(venueID);
  const venueEventsQuery = useVenueEvents(venueID);
  const { refetch: refetchVenueDetail } = venueDetailQuery;
  const viewedVenueIDRef = React.useRef<string | null>(null);
  const userCounty = session?.me.profile.last_county_name ?? null;
  const [eventPendingDelete, setEventPendingDelete] = React.useState<VenueOwnerEventSummary | null>(
    null
  );
  const deleteEventMutation = useDeleteEvent();
  const savedVenueDeal = useSavedVenueDeal(venueID);

  useFocusEffect(
    React.useCallback(() => {
      refetchVenueDetail().catch(() => undefined);
    }, [refetchVenueDetail])
  );

  const detail = venueDetailQuery.data ?? null;
  const events = venueEventsQuery.data?.events ?? [];
  const canonicalVenueHref = React.useMemo(() => {
    if (!detail) {
      return null;
    }

    return getVenueHref(
      detail.id,
      {
        ...(typeof params.source === 'string' ? { source: params.source } : {}),
        ...(typeof params.deal === 'string' ? { deal: params.deal } : {}),
      },
      { slug: detail.slug, name: detail.name }
    );
  }, [detail, params.deal, params.source]);

  React.useEffect(() => {
    if (Platform.OS !== 'web' || !canonicalVenueHref || !params.id) {
      return;
    }

    if (params.id === canonicalVenueHref.params?.id) {
      return;
    }

    // Replace legacy venue URLs only after detail loads, when the slug-safe canonical href is known.
    router.replace(canonicalVenueHref);
  }, [canonicalVenueHref, params.id]);
  const isManagedVenueAccount = Boolean(
    session?.me.profile.is_venue && detail && session.me.profile.venue_id === detail.id
  );
  const isManagedByViewerOrg = React.useMemo(
    () =>
      Boolean(
        detail &&
        (isManagedVenueAccount ||
          detail.viewer_is_owner ||
          membershipsQuery.data?.memberships.some((membership) =>
            membership.venues.some((venue) => venue.venue_id === detail.id)
          ))
      ),
    [detail, isManagedVenueAccount, membershipsQuery.data?.memberships]
  );
  const ownerOverviewQuery = useVenueOwnerOverview(venueID, detail?.viewer_is_owner ?? false);
  const ownerOverview = ownerOverviewQuery.data ?? null;
  const venueFollow = useVenueFollow(
    venueID,
    detail?.claimed_by_username ?? null,
    Boolean(detail?.claimed_by_username) && !detail?.viewer_is_owner
  );
  const category = resolveVenueCategory(detail?.category);
  const categoryConfig = getVenueCategoryConfig(category);
  const CategoryIcon = categoryConfig.icon;
  const venueUnavailableCopy = getAPIErrorRecoveryCopy(
    venueDetailQuery.error ?? venueEventsQuery.error,
    'venue',
    'This venue could not be loaded right now.'
  );
  const venueEventsErrorCopy = getAPIErrorRecoveryCopy(
    venueEventsQuery.error,
    'event',
    'Unable to load venue events.'
  );
  const venueToolsErrorCopy = getAPIErrorRecoveryCopy(
    ownerOverviewQuery.error,
    'workspace',
    'Unable to load venue tools.'
  );
  const isLoading = venueDetailQuery.isPending;
  const followerCount = detail?.follower_count ?? 0;
  const hasVenueAgeRestriction =
    detail?.age_restriction != null && detail.age_restriction !== 'all_ages';
  const weeklyStats = ownerOverview?.stats ?? {
    views: 0,
    rsvps: 0,
    followers: followerCount,
  };
  const ownerUpcomingEvents = ownerOverview?.upcoming_events ?? [];
  const ownerPastEvents = ownerOverview?.past_events ?? [];
  const venueDeals = React.useMemo(() => sortDealsByTiming(detail?.deals ?? []), [detail?.deals]);
  const canFollowVenue = Boolean(
    session && detail?.claimed_by_username && detail.claimed && !detail.viewer_is_owner
  );
  const venueBackstageAction = React.useMemo(
    () =>
      detail
        ? getVenueBackstageAction({
            backstageBaseURL: APP_CONFIG.backstageURL,
            venueID: detail.id,
            claimed: detail.claimed,
            managedByViewer: isManagedByViewerOrg,
          })
        : null,
    [detail, isManagedByViewerOrg]
  );
  const isDesktopWeb = Platform.OS === 'web' && isDesktop;
  const venuePageAnalyticsSurface = Platform.OS === 'web' ? 'web' : 'mobile_venue_page';
  const venuePageLinkSurface = Platform.OS === 'web' ? 'web_venue_page' : 'venue_page';

  React.useEffect(() => {
    if (!detail || viewedVenueIDRef.current === detail.id) {
      return;
    }

    viewedVenueIDRef.current = detail.id;
    trackVenueProfileView({
      source,
      surface: venuePageAnalyticsSurface,
      venueID: detail.id,
      venueName: detail.name,
      userCounty,
    });
  }, [detail, source, userCounty, venuePageAnalyticsSurface]);

  const handleShare = React.useCallback(() => {
    if (!detail) {
      return;
    }

    const venueLink = buildVenueLink(detail.id, { slug: detail.slug, name: detail.name });

    shareLink({
      message: venueLink,
      url: venueLink,
    })
      .then((result) => {
        if (result === 'failed') {
          return;
        }

        trackVenueShare(detail.name, detail.id, { surface: venuePageAnalyticsSurface });
      })
      .catch(() => undefined);
  }, [detail, venuePageAnalyticsSurface]);

  const handleShareDeal = React.useCallback(
    (deal: VenuePublicDeal) => {
      if (!detail) {
        return;
      }

      const venueLink = buildVenueLink(detail.id, { slug: detail.slug, name: detail.name });
      shareLink({
        message: `${deal.title} at ${detail.name}: ${venueLink}`,
        url: venueLink,
      })
        .then((result) => {
          if (result === 'failed') {
            return;
          }

          trackVenueShare(detail.name, detail.id, { surface: venuePageAnalyticsSurface });
          trackDealShare({
            dealID: deal.id,
            source: 'venue_page',
            surface: venuePageAnalyticsSurface,
            venueID: detail.id,
          });
        })
        .catch(() => undefined);
    },
    [detail, venuePageAnalyticsSurface]
  );

  const handleOpenDealDirections = React.useCallback(
    (deal: VenuePublicDeal) => {
      if (!detail) {
        return;
      }

      trackVenueDirectionsTapped({
        surface: venuePageLinkSurface,
        venueID: detail.id,
        venueName: detail.name,
        userCounty,
      });
      trackDealDirectionsTapped({
        dealID: deal.id,
        source: 'venue_page',
        surface: venuePageAnalyticsSurface,
        venueID: detail.id,
      });

      const latitude = deal.latitude ?? detail.lat;
      const longitude = deal.longitude ?? detail.lng;
      if (latitude != null && longitude != null) {
        openDirections(latitude, longitude).catch(() => undefined);
        return;
      }

      if (detail.address) {
        openAddressInMaps(detail.address);
      }
    },
    [detail, userCounty, venuePageAnalyticsSurface, venuePageLinkSurface]
  );

  const handleOpenExternalLink = React.useCallback(
    (
      value: string | undefined,
      kind: 'booking' | 'instagram' | 'social' | 'website',
      platform?: string
    ) => {
      if (!detail || !value) {
        return;
      }

      const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`;
      if (kind === 'website') {
        trackVenueWebsiteTapped({
          venueID: detail.id,
          venueName: detail.name,
          surface: venuePageLinkSurface,
        });
      } else if (kind === 'booking') {
        trackVenueBookingTapped({
          surface: venuePageAnalyticsSurface,
          venueID: detail.id,
          venueName: detail.name,
        });
      } else if (kind === 'instagram') {
        trackVenueInstagramTapped({
          venueID: detail.id,
          venueName: detail.name,
          surface: venuePageLinkSurface,
        });
      } else {
        trackVenueSocialLinkTapped({
          platform: platform ?? 'unknown',
          surface: venuePageAnalyticsSurface,
          venueID: detail.id,
          venueName: detail.name,
        });
      }
      Linking.openURL(normalized).catch(() => undefined);
    },
    [detail, venuePageAnalyticsSurface, venuePageLinkSurface]
  );

  const handleOpenPhone = React.useCallback(
    (value?: string) => {
      if (!detail || !value) {
        return;
      }

      Linking.openURL(`tel:${value}`).catch(() => undefined);
    },
    [detail]
  );

  const handleOpenDirections = React.useCallback(() => {
    if (!detail?.address) {
      return;
    }

    trackVenueDirectionsTapped({
      surface: venuePageLinkSurface,
      venueID: detail.id,
      venueName: detail.name,
      userCounty,
    });
    openAddressInMaps(detail.address);
  }, [detail, userCounty, venuePageLinkSurface]);

  const handleOpenBackstage = React.useCallback(() => {
    if (!detail || !venueBackstageAction) {
      return;
    }

    if (venueBackstageAction.kind === 'claim') {
      trackVenueClaimStarted({
        source: 'venue_page',
        venueID: detail.id,
        venueName: detail.name,
      });
    }

    Linking.openURL(venueBackstageAction.url).catch(() => undefined);
  }, [detail, venueBackstageAction]);

  const handleEditVenue = React.useCallback(() => {
    if (!detail?.viewer_is_owner) {
      return;
    }

    router.push(`/venues/${detail.id}/edit`);
  }, [detail?.id, detail?.viewer_is_owner]);

  const handleAddEvent = React.useCallback(() => {
    if (!detail) {
      return;
    }

    router.push({
      pathname: '/events/create',
      params: {
        venueId: detail.id,
        venueName: detail.name,
        address: detail.address || undefined,
        latitude: detail.lat ? String(detail.lat) : undefined,
        longitude: detail.lng ? String(detail.lng) : undefined,
      },
    });
  }, [detail]);

  const handleOpenFollowers = React.useCallback(() => {
    if (!detail?.viewer_is_owner) {
      return;
    }

    router.push({
      pathname: '/followers',
      params: {
        venueId: detail.id,
        venueName: detail.name,
        count: String(detail.follower_count ?? 0),
      },
    });
  }, [detail]);

  const handleToggleFollowVenue = React.useCallback(async () => {
    if (!detail?.claimed_by_username || !canFollowVenue) {
      return;
    }

    await venueFollow.toggleFollow.mutateAsync({
      isFollowing: venueFollow.isFollowing,
    });

    if (venueFollow.isFollowing) {
      trackVenueUnfollow(detail.name, detail.id, {
        profileUsername: detail.claimed_by_username,
        surface: venuePageAnalyticsSurface,
      });
      return;
    }

    trackVenueFollow(detail.name, detail.id, {
      profileUsername: detail.claimed_by_username,
      surface: venuePageAnalyticsSurface,
    });
  }, [
    canFollowVenue,
    detail,
    venueFollow.isFollowing,
    venueFollow.toggleFollow,
    venuePageAnalyticsSurface,
  ]);

  const handleConfirmDeleteEvent = React.useCallback(async () => {
    if (!eventPendingDelete) {
      return;
    }

    try {
      await deleteEventMutation.mutateAsync({
        eventID: eventPendingDelete.id,
        venueID: detail?.id ?? null,
      });
      setEventPendingDelete(null);
    } catch {}
  }, [deleteEventMutation, detail?.id, eventPendingDelete]);
  const handleOpenEventDetail = React.useCallback((eventID: string) => {
    router.navigate(getEventHref(eventID) as never);
  }, []);
  const handleEditOwnedEvent = React.useCallback((eventID: string) => {
    router.push(`/events/${eventID}/edit`);
  }, []);
  const handlePromptDeleteEvent = React.useCallback((event: VenueOwnerEventSummary) => {
    setEventPendingDelete(event);
  }, []);

  const socialLinks = React.useMemo(() => {
    if (detail?.social_links?.length) {
      return detail.social_links;
    }

    if (!detail?.instagram_handle) {
      return [];
    }

    return [
      {
        platform: 'instagram',
        url: `https://instagram.com/${detail.instagram_handle.replace(/^@/, '')}`,
      } satisfies VenueSocialLink,
    ];
  }, [detail?.instagram_handle, detail?.social_links]);

  if (isLoading && !detail) {
    return <VenuePageLoadingSkeleton venueName={loadingVenueName} />;
  }

  if (!detail) {
    return (
      <AppScreen>
        <Stack.Screen options={{ headerShown: false }} />
        <UnavailableState
          eyebrow="Venue Link"
          icon={StoreIcon}
          title="This venue page is unavailable"
          body="That spot is not public right now, or the link is stale. Head back to the feed and we will help you find what is still on."
          detail={venueUnavailableCopy}
          primaryActionLabel="Browse events"
          onPrimaryAction={() => router.replace('/(tabs)/events')}
          secondaryActionLabel="Open map"
          onSecondaryAction={() => router.replace('/(tabs)/map')}
        />
      </AppScreen>
    );
  }

  if (isDesktopWeb && !detail.viewer_is_owner) {
    return (
      <AppScreen className="bg-background" edges={['top']}>
        <Stack.Screen options={{ headerShown: false }} />
        <AppTopBar pulseBadgeLabel={null} />
        <ScrollView
          contentContainerStyle={{
            paddingBottom: 72,
          }}
          showsVerticalScrollIndicator={false}>
          <View className="mx-auto w-full max-w-[1180px] gap-10 px-8 pb-8 pt-8">
            <View className="flex-row items-center justify-between">
              <Button
                variant="ghost"
                className="rounded-full px-4"
                onPress={() => {
                  if (router.canGoBack()) {
                    router.back();
                    return;
                  }
                  router.replace('/(tabs)/events');
                }}>
                <Icon as={ArrowLeftIcon} size={16} className="text-foreground" />
                <Text>Back</Text>
              </Button>
              <Button variant="outline" className="rounded-full px-4" onPress={handleShare}>
                <Icon as={ShareIcon} size={15} className="text-foreground" />
                <Text>Share venue</Text>
              </Button>
            </View>

            <View className="overflow-hidden rounded-[36px] border border-border/70">
              {detail.cover_url ? (
                <FocalEventPosterImage
                  image={{
                    public_url: detail.cover_url,
                    focal_point_x: detail.cover_focal_point_x,
                    focal_point_y: detail.cover_focal_point_y,
                    focal_scale: detail.cover_focal_scale,
                  }}
                  style={{ height: 360, width: '100%' }}
                />
              ) : (
                <View
                  className="h-[360px] overflow-hidden"
                  style={{ backgroundColor: `#${categoryConfig.bgColor}` }}>
                  <LinearGradient
                    colors={[
                      `#${categoryConfig.bgColor}`,
                      `#${categoryConfig.bgColor}`,
                      `#${categoryConfig.color}`,
                    ]}
                    locations={[0.05, 0.58, 1]}
                    start={{ x: 0.02, y: 0.08 }}
                    end={{ x: 1, y: 0.94 }}
                    style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
                  />
                  <LinearGradient
                    colors={['rgba(255,255,255,0.24)', 'rgba(255,255,255,0.06)', 'transparent']}
                    locations={[0, 0.28, 0.72]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 0.82, y: 0.78 }}
                    style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
                  />
                  <LinearGradient
                    colors={['rgba(12,7,4,0)', 'rgba(12,7,4,0.22)', 'rgba(12,7,4,0.52)']}
                    locations={[0.24, 0.72, 1]}
                    start={{ x: 0.28, y: 0.08 }}
                    end={{ x: 1, y: 1 }}
                    style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
                  />
                </View>
              )}

              <View className="absolute inset-x-0 bottom-0 px-8 pb-8">
                <View className="max-w-[760px] gap-4">
                  <Text variant="small" className="uppercase tracking-[2.2px] text-white/70">
                    Venue
                  </Text>
                  <View className="flex-row items-end gap-5">
                    <View
                      className="relative size-[104px] items-center justify-center rounded-full border-[5px]"
                      style={{
                        borderColor: DESIGN_TOKENS.colors.canvas,
                        backgroundColor: `#${categoryConfig.bgColor}`,
                      }}>
                      {detail.logo_url ? (
                        <FocalEventPosterImage
                          className="rounded-full"
                          image={{
                            public_url: detail.logo_url,
                            focal_point_x: detail.logo_focal_point_x,
                            focal_point_y: detail.logo_focal_point_y,
                            focal_scale: detail.logo_focal_scale,
                          }}
                          style={{ height: 92, width: 92 }}
                        />
                      ) : (
                        <Icon as={CategoryIcon} size={34} color={`#${categoryConfig.color}`} />
                      )}
                      {detail.is_verified ? (
                        <View
                          className="absolute bottom-0 right-0 size-7 items-center justify-center rounded-full border-[4px] bg-background"
                          style={{ borderColor: DESIGN_TOKENS.colors.canvas }}>
                          <Icon as={BadgeCheckIcon} size={20} className="text-primary" />
                        </View>
                      ) : null}
                    </View>
                    <View className="flex-1 gap-3">
                      <Text className="text-[56px] font-extrabold leading-[58px] tracking-tight text-white">
                        {detail.name}
                      </Text>
                      <View className="flex-row flex-wrap items-center gap-x-4 gap-y-2">
                        {category ? (
                          <Text
                            variant="small"
                            className="font-semibold uppercase tracking-[1.8px]"
                            style={{ color: `#${categoryConfig.color}` }}>
                            {categoryConfig.label}
                          </Text>
                        ) : null}
                        <Text variant="small" style={{ color: 'rgba(255,255,255,0.82)' }}>
                          {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
                        </Text>
                        {detail.event_count > 0 ? (
                          <Text variant="small" style={{ color: 'rgba(255,255,255,0.82)' }}>
                            {detail.event_count} {detail.event_count === 1 ? 'event' : 'events'}
                          </Text>
                        ) : null}
                        {hasVenueAgeRestriction ? (
                          <Text variant="small" style={{ color: 'rgba(255,255,255,0.82)' }}>
                            {formatEventAgeRestriction(detail.age_restriction)}
                          </Text>
                        ) : null}
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>

            <View className="flex-row items-start gap-12">
              <View className="min-w-0 flex-1 gap-10">
                <View className="gap-5">
                  <View className="flex-row flex-wrap gap-3">
                    {canFollowVenue ? (
                      <Button
                        variant={venueFollow.isFollowing ? 'outline' : 'default'}
                        className="rounded-full px-5"
                        disabled={venueFollow.toggleFollow.isPending || venueFollow.isLoading}
                        onPress={() => handleToggleFollowVenue().catch(() => undefined)}>
                        <Icon
                          as={UsersIcon}
                          size={16}
                          className={
                            venueFollow.isFollowing
                              ? 'text-muted-foreground'
                              : 'text-primary-foreground'
                          }
                        />
                        <Text>{venueFollow.isFollowing ? 'Following' : 'Follow venue'}</Text>
                      </Button>
                    ) : null}

                    {venueBackstageAction ? (
                      <Button
                        variant="outline"
                        className="rounded-full border-primary/30 px-5"
                        onPress={handleOpenBackstage}>
                        <Text>{venueBackstageAction.buttonLabel}</Text>
                      </Button>
                    ) : null}
                  </View>

                  {detail.description ? (
                    <Text className="max-w-[760px] text-[19px] leading-8 text-foreground">
                      {detail.description}
                    </Text>
                  ) : (
                    <Text className="max-w-[760px] text-[19px] leading-8 text-muted-foreground">
                      A room on Radious worth keeping an eye on.
                    </Text>
                  )}

                  {venueBackstageAction ? (
                    <View className="border-l-2 border-primary/35 pl-4">
                      <Text variant="small" className="uppercase tracking-[1.8px] text-primary">
                        {venueBackstageAction.kind === 'claim'
                          ? 'Business tools live in Backstage'
                          : 'This venue is connected to your business'}
                      </Text>
                      <Text
                        variant="small"
                        className="mt-2 max-w-[540px] leading-6 text-muted-foreground">
                        {venueBackstageAction.helperText}
                      </Text>
                    </View>
                  ) : null}

                  {detail.tags.length > 0 ? (
                    <View className="flex-row flex-wrap gap-2">
                      {detail.tags.map((tag) => (
                        <MetaBadge key={tag} label={getEventTagLabel(tag)} />
                      ))}
                    </View>
                  ) : null}
                </View>

                <VenueDealsSection
                  deals={venueDeals}
                  isSavingDeal={savedVenueDeal.isPending}
                  onDirections={handleOpenDealDirections}
                  onSave={
                    session
                      ? (deal) => {
                          savedVenueDeal.mutate({ deal });
                          trackDealSaved({
                            dealID: deal.id,
                            isSaved: !deal.is_saved,
                            source: 'venue_page',
                            surface: venuePageAnalyticsSurface,
                            venueID: deal.venue_id,
                          });
                        }
                      : undefined
                  }
                  onShare={handleShareDeal}
                />

                <View className="gap-4">
                  <View className="flex-row items-end justify-between gap-4 pb-4">
                    <View className="gap-2">
                      <Text
                        variant="small"
                        className="uppercase tracking-[1.8px] text-muted-foreground">
                        Lineup
                      </Text>
                      <Text className="text-xl font-bold tracking-tight text-foreground">
                        Upcoming at {detail.name}
                      </Text>
                    </View>
                    {detail.event_count > 0 ? (
                      <Text variant="small" className="text-muted-foreground">
                        {detail.event_count} scheduled
                      </Text>
                    ) : null}
                  </View>

                  {venueEventsQuery.isPending ? (
                    <LoadingState label="Loading upcoming events..." />
                  ) : venueEventsQuery.isError ? (
                    <ErrorState
                      title="Couldn't load venue events."
                      body={venueEventsErrorCopy}
                      actionLabel="Retry"
                      onAction={() => venueEventsQuery.refetch().catch(() => undefined)}
                    />
                  ) : events.length > 0 ? (
                    <View className="border-t border-border">
                      {events.map((event) => (
                        <DesktopVenueEventRow
                          key={event.id}
                          event={event}
                          onOpenEvent={handleOpenEventDetail}
                        />
                      ))}
                    </View>
                  ) : (
                    <EmptyState
                      title="No upcoming events yet."
                      body="This venue doesn't have anything scheduled right now."
                    />
                  )}
                </View>
              </View>

              <View className="w-[320px] shrink-0 gap-8">
                <View className="gap-4">
                  <Text
                    variant="small"
                    className="uppercase tracking-[1.8px] text-muted-foreground">
                    Visit
                  </Text>
                  <View className="border-t border-border">
                    {detail.address ? (
                      <DesktopVenueInfoRow
                        icon={MapPinIcon}
                        label={detail.address}
                        detail="Directions"
                        onPress={handleOpenDirections}
                      />
                    ) : null}
                    {detail.website ? (
                      <DesktopVenueInfoRow
                        icon={Globe}
                        label={formatVenueURL(detail.website)}
                        detail="Website"
                        onPress={() => handleOpenExternalLink(detail.website, 'website')}
                      />
                    ) : null}
                    {detail.phone ? (
                      <DesktopVenueInfoRow
                        icon={Phone}
                        label={detail.phone}
                        detail="Call"
                        onPress={() => handleOpenPhone(detail.phone)}
                      />
                    ) : null}
                    {detail.booking_url ? (
                      <DesktopVenueInfoRow
                        icon={ExternalLink}
                        label={formatVenueURL(detail.booking_url)}
                        detail="Booking"
                        onPress={() => handleOpenExternalLink(detail.booking_url, 'booking')}
                      />
                    ) : null}
                    {socialLinks.map((link) => (
                      <DesktopVenueInfoRow
                        key={`${link.platform}-${link.url}`}
                        icon={getVenueSocialIcon(link.platform)}
                        label={getVenueSocialLabel(link)}
                        detail={link.platform}
                        onPress={() =>
                          handleOpenExternalLink(
                            link.url,
                            link.platform === 'instagram' ? 'instagram' : 'social',
                            link.platform
                          )
                        }
                      />
                    ))}
                  </View>
                </View>

                {detail.hours.length > 0 ? (
                  <View className="gap-4">
                    <Text
                      variant="small"
                      className="uppercase tracking-[1.8px] text-muted-foreground">
                      Hours
                    </Text>
                    <View className="border-t border-border">
                      {detail.hours.map((hour) => (
                        <View
                          key={hour.day}
                          className="flex-row items-center justify-between border-b border-border/80 py-3">
                          <Text variant="small" className="font-medium capitalize text-foreground">
                            {hour.day}
                          </Text>
                          <Text variant="small" className="text-muted-foreground">
                            {formatVenueHour(hour)}
                          </Text>
                        </View>
                      ))}
                    </View>
                  </View>
                ) : null}
              </View>
            </View>
          </View>
        </ScrollView>

        <AlertDialog
          open={Boolean(eventPendingDelete)}
          onOpenChange={(open) => !open && setEventPendingDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this event?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes {eventPendingDelete?.title ?? 'this event'} from Radious.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-row justify-end gap-3">
              <AlertDialogAction
                disabled={deleteEventMutation.isPending}
                onPress={handleConfirmDeleteEvent}
                className="bg-destructive">
                <Text>Delete Event</Text>
              </AlertDialogAction>
              <AlertDialogCancel disabled={deleteEventMutation.isPending}>
                <Text>Keep Event</Text>
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AppScreen>
    );
  }

  return (
    <AppScreen>
      <Stack.Screen options={{ headerShown: false }} />
      <ScrollView
        contentContainerStyle={{ paddingBottom: 120 }}
        showsVerticalScrollIndicator={false}>
        <View className="relative">
          {detail.cover_url ? (
            <View style={{ height: 240 }}>
              <FocalEventPosterImage
                image={{
                  public_url: detail.cover_url,
                  focal_point_x: detail.cover_focal_point_x,
                  focal_point_y: detail.cover_focal_point_y,
                  focal_scale: detail.cover_focal_scale,
                }}
                style={{ height: 240, width: '100%' }}
              />
              <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.44)']}
                style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 110 }}
              />
            </View>
          ) : (
            <View
              style={{
                height: 240,
                overflow: 'hidden',
                backgroundColor: `#${categoryConfig.bgColor}`,
              }}>
              <LinearGradient
                colors={[
                  `#${categoryConfig.bgColor}`,
                  `#${categoryConfig.bgColor}`,
                  `#${categoryConfig.color}`,
                ]}
                locations={[0.05, 0.58, 1]}
                start={{ x: 0.02, y: 0.08 }}
                end={{ x: 1, y: 0.94 }}
                style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
              />
              <LinearGradient
                colors={['rgba(255,255,255,0.26)', 'rgba(255,255,255,0.06)', 'transparent']}
                locations={[0, 0.28, 0.72]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0.82, y: 0.78 }}
                style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
              />
              <LinearGradient
                colors={['rgba(12,7,4,0)', 'rgba(12,7,4,0.16)', 'rgba(12,7,4,0.4)']}
                locations={[0.24, 0.72, 1]}
                start={{ x: 0.15, y: 0.08 }}
                end={{ x: 1, y: 1 }}
                style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
              />
              <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.44)']}
                style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 110 }}
              />
            </View>
          )}
          <View className="absolute left-0 right-0 top-0 flex-row items-center justify-between px-4 pt-12">
            <IconAction
              icon={ArrowLeftIcon}
              onPress={() => {
                if (router.canGoBack()) {
                  router.back();
                  return;
                }
                router.replace('/(tabs)/events');
              }}
            />
            <IconAction icon={ShareIcon} onPress={handleShare} />
          </View>
        </View>

        <View className="-mt-12 px-4">
          <View
            className="size-[88px] items-center justify-center rounded-full border-4"
            style={{
              borderColor: DESIGN_TOKENS.colors.canvas,
              backgroundColor: `#${categoryConfig.bgColor}`,
            }}>
            {detail.logo_url ? (
              <FocalEventPosterImage
                className="rounded-full"
                image={{
                  public_url: detail.logo_url,
                  focal_point_x: detail.logo_focal_point_x,
                  focal_point_y: detail.logo_focal_point_y,
                  focal_scale: detail.logo_focal_scale,
                }}
                style={{ height: 80, width: 80 }}
              />
            ) : (
              <Icon as={CategoryIcon} size={28} color={`#${categoryConfig.color}`} />
            )}
            {detail.is_verified ? (
              <View
                className="absolute bottom-0 right-0 size-[26px] items-center justify-center rounded-full border-4 bg-background"
                style={{ borderColor: DESIGN_TOKENS.colors.canvas }}>
                <Icon as={BadgeCheckIcon} size={18} className="text-primary" />
              </View>
            ) : null}
          </View>
        </View>

        <View className="gap-1.5 px-4 pt-3">
          <Text
            className="text-[36px] font-extrabold leading-[40px] tracking-tight text-foreground"
            numberOfLines={2}
            adjustsFontSizeToFit
            minimumFontScale={0.72}>
            {detail.name}
          </Text>
          <View className="flex-row flex-wrap items-center gap-x-1.5 gap-y-1">
            {category ? (
              <Text
                variant="small"
                className="font-semibold uppercase tracking-[1.6px]"
                style={{ color: `#${categoryConfig.color}` }}>
                {categoryConfig.label}
              </Text>
            ) : null}
            {category ? (
              <Text variant="small" className="text-muted-foreground">
                ·
              </Text>
            ) : null}
            {detail.viewer_is_owner ? (
              <Pressable onPress={handleOpenFollowers}>
                <Text variant="small" className="text-muted-foreground">
                  {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
                </Text>
              </Pressable>
            ) : (
              <Text variant="small" className="text-muted-foreground">
                {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
              </Text>
            )}
            {detail.event_count > 0 ? (
              <>
                <Text variant="small" className="text-muted-foreground">
                  ·
                </Text>
                <Text variant="small" className="text-muted-foreground">
                  {detail.event_count} {detail.event_count === 1 ? 'event' : 'events'}
                </Text>
              </>
            ) : null}
            {hasVenueAgeRestriction ? (
              <>
                <Text variant="small" className="text-muted-foreground">
                  ·
                </Text>
                <Text variant="small" className="text-muted-foreground">
                  {formatEventAgeRestriction(detail.age_restriction)}
                </Text>
              </>
            ) : null}
          </View>
        </View>

        <View className="flex-row flex-wrap gap-2.5 px-4 pt-4">
          {canFollowVenue ? (
            <Button
              variant={venueFollow.isFollowing ? 'outline' : 'default'}
              className="px-5"
              disabled={venueFollow.toggleFollow.isPending || venueFollow.isLoading}
              onPress={() => handleToggleFollowVenue().catch(() => undefined)}>
              <Icon
                as={UsersIcon}
                size={15}
                className={
                  venueFollow.isFollowing ? 'text-muted-foreground' : 'text-primary-foreground'
                }
              />
              <Text>{venueFollow.isFollowing ? 'Following' : 'Follow Venue'}</Text>
            </Button>
          ) : null}
          {venueBackstageAction ? (
            <Button variant="outline" className="px-5" onPress={handleOpenBackstage}>
              <Text>{venueBackstageAction.buttonLabel}</Text>
            </Button>
          ) : null}
          {detail.viewer_is_owner ? (
            <Button className="px-5" onPress={handleEditVenue}>
              <Icon as={PencilIcon} size={15} className="text-primary-foreground" />
              <Text>Edit venue</Text>
            </Button>
          ) : null}
        </View>

        {detail.description ? (
          <Text variant="muted" className="px-4 pt-5 leading-[26px]">
            {detail.description}
          </Text>
        ) : null}

        <VenueActivitySpotlight
          deals={venueDeals}
          events={events}
          highlightedDealID={highlightedDealID}
          onOpenEvent={handleOpenEventDetail}
        />

        {venueBackstageAction ? (
          <View className="mx-4 mt-4 border-l-2 border-primary/35 pl-3.5">
            <Text variant="small" className="font-semibold text-foreground">
              {venueBackstageAction.kind === 'claim'
                ? 'Business tools live in Backstage'
                : 'This venue is connected to your business'}
            </Text>
            <Text variant="small" className="mt-0.5 leading-5 text-muted-foreground">
              {venueBackstageAction.helperText}
            </Text>
          </View>
        ) : null}

        <View className="mx-4 mt-6 border-t border-border/40" />

        {detail.address ||
        detail.website ||
        detail.phone ||
        detail.booking_url ||
        socialLinks.length > 0 ? (
          <View className="mt-5">
            <Text
              variant="small"
              className="mb-1.5 px-4 font-semibold uppercase tracking-[1.6px] text-muted-foreground">
              Visit
            </Text>
            {detail.address ? (
              <ContactRow
                icon={MapPinIcon}
                label={detail.address}
                actionLabel="Directions"
                accent
                onPress={handleOpenDirections}
              />
            ) : null}
            {detail.website ? (
              <ContactRow
                icon={Globe}
                label={formatVenueURL(detail.website)}
                actionLabel="Website"
                onPress={() => handleOpenExternalLink(detail.website, 'website')}
              />
            ) : null}
            {detail.phone ? (
              <ContactRow
                icon={Phone}
                label={detail.phone}
                actionLabel="Call"
                onPress={() => handleOpenPhone(detail.phone)}
              />
            ) : null}
            {detail.booking_url ? (
              <ContactRow
                icon={ExternalLink}
                label={formatVenueURL(detail.booking_url)}
                actionLabel="Booking"
                onPress={() => handleOpenExternalLink(detail.booking_url, 'booking')}
              />
            ) : null}
            {socialLinks.map((link) => (
              <ContactRow
                key={`${link.platform}-${link.url}`}
                icon={getVenueSocialIcon(link.platform)}
                label={getVenueSocialLabel(link)}
                actionLabel={getVenueSocialActionLabel(link.platform)}
                onPress={() =>
                  handleOpenExternalLink(
                    link.url,
                    link.platform === 'instagram' ? 'instagram' : 'social',
                    link.platform
                  )
                }
              />
            ))}
          </View>
        ) : null}

        {detail.hours.length > 0 ? (
          <View className="mt-5">
            <View className="mb-1.5 flex-row items-center gap-2 px-4">
              <Icon as={Clock3} size={13} className="text-primary" />
              <Text
                variant="small"
                className="font-semibold uppercase tracking-[1.6px] text-muted-foreground">
                Hours
              </Text>
            </View>
            {detail.hours.map((hour, index) => (
              <View
                key={hour.day}
                className={index > 0 ? 'flex-row items-center justify-between border-t border-border/40 px-4 py-2.5' : 'flex-row items-center justify-between px-4 py-2.5'}>
                <Text variant="small" className="font-medium text-foreground">
                  {getVenueDayLabel(hour.day)}
                </Text>
                <Text variant="small" className="text-muted-foreground">
                  {formatVenueHour(hour)}
                </Text>
              </View>
            ))}
          </View>
        ) : null}

        {detail.viewer_is_owner ? (
          <View className="px-4 pt-5">
            <OwnerVenueStatsBar
              views={weeklyStats.views}
              rsvps={weeklyStats.rsvps}
              followers={weeklyStats.followers}
            />
          </View>
        ) : null}

        {detail.viewer_is_owner ? (
          <View className="gap-3 px-4 pt-6">
            <View className="flex-row items-center justify-between">
              <Text className="text-xl font-bold text-foreground">Your Events</Text>
              <Button size="sm" className="px-4" onPress={handleAddEvent}>
                <Icon as={PlusIcon} size={14} className="text-primary-foreground" />
                <Text>Add event</Text>
              </Button>
            </View>
            <View className="gap-4">
              {ownerOverviewQuery.isPending ? (
                <LoadingState label="Loading venue tools..." />
              ) : ownerOverviewQuery.isError ? (
                <ErrorState
                  title="Couldn't load venue tools."
                  body={venueToolsErrorCopy}
                  actionLabel="Retry"
                  onAction={() => ownerOverviewQuery.refetch().catch(() => undefined)}
                />
              ) : (
                <View className="gap-5">
                  <View className="gap-3">
                    <Text className="text-lg font-semibold text-foreground">Upcoming</Text>
                    {ownerUpcomingEvents.length > 0 ? (
                      <View className="gap-3">
                        {ownerUpcomingEvents.map((event) => (
                          <VenueOwnerEventCard
                            key={event.id}
                            event={event}
                            mode="upcoming"
                            onOpenEvent={handleOpenEventDetail}
                            onDeleteEvent={handlePromptDeleteEvent}
                            onEditEvent={handleEditOwnedEvent}
                          />
                        ))}
                      </View>
                    ) : (
                      <EmptyState
                        title="No upcoming events yet."
                        body="No upcoming events — add your first one."
                        actionLabel="Add Event"
                        onAction={handleAddEvent}
                      />
                    )}
                  </View>
                  {ownerPastEvents.length > 0 ? (
                    <View className="gap-3">
                      <Text className="text-lg font-semibold text-foreground">Past 30 Days</Text>
                      <View className="gap-3">
                        {ownerPastEvents.map((event) => (
                          <VenueOwnerEventCard
                            key={event.id}
                            event={event}
                            mode="past"
                            onOpenEvent={handleOpenEventDetail}
                          />
                        ))}
                      </View>
                    </View>
                  ) : null}
                </View>
              )}
            </View>
          </View>
        ) : venueEventsQuery.isPending ? (
          <View className="px-4 pt-7">
            <LoadingState label="Loading upcoming events..." />
          </View>
        ) : venueEventsQuery.isError ? (
          <View className="px-4 pt-7">
            <ErrorState
              title="Couldn't load venue events."
              body={venueEventsErrorCopy}
              actionLabel="Retry"
              onAction={() => venueEventsQuery.refetch().catch(() => undefined)}
            />
          </View>
        ) : events.length > 0 ? (
          <VenueUpcomingPreview
            events={events}
            venueName={detail.name}
            onOpenEvent={handleOpenEventDetail}
          />
        ) : (
          <View className="px-4 pt-7">
            <EmptyState
              title="No upcoming events yet."
              body="This venue doesn't have anything scheduled right now."
            />
          </View>
        )}

        <VenueDealsPreview
          deals={venueDeals}
          isSavingDeal={savedVenueDeal.isPending}
          onSave={
            session
              ? (deal) => {
                  savedVenueDeal.mutate({ deal });
                  trackDealSaved({
                    dealID: deal.id,
                    isSaved: !deal.is_saved,
                    source: 'venue_page',
                    surface: venuePageAnalyticsSurface,
                    venueID: deal.venue_id,
                  });
                }
              : undefined
          }
        />

        {detail.tags.length > 0 ? (
          <View className="px-4 pt-7">
            <Text variant="small" className="mb-2.5 font-semibold uppercase tracking-[1.6px] text-muted-foreground">
              Good for
            </Text>
            <View className="flex-row flex-wrap gap-2">
              {detail.tags.map((tag) => (
                <MetaBadge key={tag} label={getEventTagLabel(tag)} />
              ))}
            </View>
          </View>
        ) : null}
      </ScrollView>

      <AlertDialog
        open={Boolean(eventPendingDelete)}
        onOpenChange={(open) => !open && setEventPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {eventPendingDelete?.title ?? 'this event'} from Radious.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-end gap-3">
            <AlertDialogAction
              disabled={deleteEventMutation.isPending}
              onPress={handleConfirmDeleteEvent}
              className="bg-destructive">
              <Text>Delete Event</Text>
            </AlertDialogAction>
            <AlertDialogCancel disabled={deleteEventMutation.isPending}>
              <Text>Keep Event</Text>
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppScreen>
  );
}

function formatVenueURL(value: string) {
  try {
    const parsed = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    const pathname = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
    return `${parsed.host}${pathname}`;
  } catch {
    return value;
  }
}

function getVenueDayLabel(day: VenueHour['day']) {
  return day.charAt(0).toUpperCase() + day.slice(1);
}

function formatVenueHour(hour: VenueHour) {
  if (hour.closed) {
    return 'Closed';
  }

  if (!hour.opens_at || !hour.closes_at) {
    return 'Hours unavailable';
  }

  return `${formatVenueTime(hour.opens_at)} - ${formatVenueTime(hour.closes_at)}`;
}

function formatVenueTime(value: string) {
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return value;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const normalizedHours = hours % 12 || 12;
  return `${normalizedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function getVenueSocialIcon(platform: VenueSocialLink['platform']) {
  switch (platform) {
    case 'instagram':
      return Instagram;
    case 'tiktok':
      return Music2;
    case 'facebook':
      return Facebook;
    case 'x':
      return AtSign;
    case 'spotify':
      return Disc3;
    case 'youtube':
      return Youtube;
    case 'linktree':
      return TreePalm;
    default:
      return Link;
  }
}

function getVenueSocialLabel(link: VenueSocialLink) {
  if (link.platform === 'instagram') {
    try {
      const pathname = new URL(link.url).pathname.replace(/^\/+|\/+$/g, '');
      if (pathname) {
        return `@${pathname}`;
      }
    } catch {}
  }

  return formatVenueURL(link.url);
}

function getVenueSocialActionLabel(platform: VenueSocialLink['platform']) {
  switch (platform) {
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'facebook':
      return 'Facebook';
    case 'x':
      return 'X';
    case 'spotify':
      return 'Spotify';
    case 'youtube':
      return 'YouTube';
    case 'linktree':
      return 'Linktree';
    default:
      return 'Link';
  }
}

function getVenueLoadingName(value?: string | string[]) {
  const raw = typeof value === 'string' ? value.trim() : '';
  const slug = raw.includes('--') ? raw.slice(0, raw.lastIndexOf('--')) : '';
  if (!slug) {
    return null;
  }

  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function VenuePageLoadingSkeleton({ venueName }: { venueName: string | null }) {
  const fallbackCategory = getVenueCategoryConfig();
  const FallbackIcon = fallbackCategory.icon;

  return (
    <AppScreen>
      <Stack.Screen options={{ headerShown: false }} />
      <ScrollView contentContainerStyle={{ paddingBottom: 120 }} showsVerticalScrollIndicator={false}>
        <View className="relative">
          <View
            className="h-[240px] overflow-hidden"
            style={{ backgroundColor: `#${fallbackCategory.bgColor}` }}>
            <LinearGradient
              colors={[
                `#${fallbackCategory.bgColor}`,
                `#${fallbackCategory.bgColor}`,
                `#${fallbackCategory.color}`,
              ]}
              locations={[0.05, 0.58, 1]}
              start={{ x: 0.02, y: 0.08 }}
              end={{ x: 1, y: 0.94 }}
              style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
            />
            <LinearGradient
              colors={['rgba(255,255,255,0.24)', 'rgba(255,255,255,0.05)', 'transparent']}
              locations={[0, 0.28, 0.72]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0.82, y: 0.78 }}
              style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
            />
          </View>
          <View className="absolute left-0 right-0 top-0 flex-row items-center justify-between px-4 pt-12">
            <IconAction
              icon={ArrowLeftIcon}
              onPress={() => {
                if (router.canGoBack()) {
                  router.back();
                  return;
                }
                router.replace('/(tabs)/events');
              }}
            />
            <IconAction icon={ShareIcon} onPress={() => undefined} />
          </View>
        </View>

        <View className="-mt-12 px-4">
          <View
            className="size-[88px] items-center justify-center rounded-full border-4"
            style={{
              borderColor: DESIGN_TOKENS.colors.canvas,
              backgroundColor: `#${fallbackCategory.bgColor}`,
            }}>
            <Icon as={FallbackIcon} size={28} color={`#${fallbackCategory.color}`} />
          </View>
        </View>

        <View className="gap-2 px-4 pt-3">
          {venueName ? (
            <Text
              className="text-[36px] font-extrabold leading-[40px] tracking-tight text-foreground"
              numberOfLines={2}
              adjustsFontSizeToFit
              minimumFontScale={0.72}>
              {venueName}
            </Text>
          ) : (
            <Skeleton className="h-10 w-3/4 rounded-[14px] bg-surface-raised" />
          )}
          <View className="flex-row items-center gap-2">
            <Skeleton className="h-4 w-16 rounded-full bg-surface-raised" />
            <Skeleton className="h-4 w-24 rounded-full bg-surface-raised" />
            <Skeleton className="h-4 w-16 rounded-full bg-surface-raised" />
          </View>
        </View>

        <View className="flex-row gap-2.5 px-4 pt-4">
          <Skeleton className="h-11 w-32 rounded-[18px] bg-surface-raised" />
          <Skeleton className="h-11 w-28 rounded-[18px] bg-surface-raised" />
        </View>

        <View className="px-4 pt-5">
          <Skeleton className="h-4 w-full rounded-full bg-surface-raised" />
          <Skeleton className="mt-3 h-4 w-[86%] rounded-full bg-surface-raised" />
        </View>

        <View className="mx-4 mt-6 rounded-[20px] border border-border bg-surface/80 p-4">
          <View className="flex-row items-center gap-3">
            <Skeleton className="size-[64px] rounded-[16px] bg-surface-raised" />
            <View className="min-w-0 flex-1">
              <Skeleton className="h-4 w-20 rounded-full bg-surface-raised" />
              <Skeleton className="mt-2 h-5 w-[88%] rounded-full bg-surface-raised" />
              <Skeleton className="mt-2 h-4 w-32 rounded-full bg-surface-raised" />
            </View>
          </View>
        </View>

        <View className="mx-4 mt-6 border-t border-border/40" />
        <View className="mt-5">
          <Skeleton className="mx-4 mb-3 h-4 w-16 rounded-full bg-surface-raised" />
          <VenueLoadingContactRow />
          <VenueLoadingContactRow />
          <VenueLoadingContactRow />
        </View>
      </ScrollView>
    </AppScreen>
  );
}

function VenueLoadingContactRow() {
  return (
    <View className="flex-row items-center gap-3.5 border-b border-border/40 px-4 py-3">
      <Skeleton className="size-4 rounded-full bg-surface-raised" />
      <View className="min-w-0 flex-1 gap-2">
        <Skeleton className="h-4 w-4/5 rounded-full bg-surface-raised" />
        <Skeleton className="h-3 w-24 rounded-full bg-surface-raised" />
      </View>
      <Skeleton className="size-4 rounded-full bg-surface-raised" />
    </View>
  );
}

function ContactRow({
  icon,
  label,
  actionLabel,
  onPress,
  accent = false,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  actionLabel: string;
  onPress: () => void;
  accent?: boolean;
}) {
  return (
    <Pressable
      className="flex-row items-center gap-3.5 border-b border-border/40 px-4 py-3"
      onPress={onPress}>
      <Icon as={icon} size={16} className={accent ? 'text-primary' : 'text-muted-foreground'} />
      <View className="min-w-0 flex-1 gap-0.5">
        <Text className="text-[15px] font-medium leading-5 text-foreground" numberOfLines={2}>
          {label}
        </Text>
        <Text variant="small" className="text-muted-foreground">
          {actionLabel}
        </Text>
      </View>
      <Icon as={ArrowRightIcon} size={15} className="text-muted-foreground" />
    </Pressable>
  );
}

function DesktopVenueInfoRow({
  icon,
  label,
  detail,
  onPress,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  detail: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      className="flex-row items-center gap-4 border-b border-border/80 py-4"
      onPress={onPress}>
      <View className="size-10 items-center justify-center rounded-full border border-border bg-input/25">
        <Icon as={icon} size={16} className="text-muted-foreground" />
      </View>
      <View className="min-w-0 flex-1 gap-1">
        <Text variant="small" className="uppercase tracking-[1.5px] text-muted-foreground">
          {detail}
        </Text>
        <Text className="text-base leading-6 text-foreground" numberOfLines={3}>
          {label}
        </Text>
      </View>
      <Icon as={ArrowRightIcon} size={16} className="text-muted-foreground" />
    </Pressable>
  );
}

const OwnerVenueStatsBar = React.memo(function OwnerVenueStatsBar({
  views,
  rsvps,
  followers,
}: {
  views: number;
  rsvps: number;
  followers: number;
}) {
  return (
    <Card
      className="border-border bg-surface-alt p-5"
      style={{
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
      }}>
      <View className="gap-4">
        <Text variant="small" className="uppercase tracking-[1.6px] text-muted-foreground">
          This week
        </Text>
        <View className="flex-row flex-wrap gap-3">
          <OwnerVenueStat icon={EyeIcon} label="Views" value={views} />
          <OwnerVenueStat icon={CheckIcon} label="Going" value={rsvps} />
          <OwnerVenueStat icon={UsersIcon} label="Audience" value={followers} />
        </View>
      </View>
    </Card>
  );
});

const OwnerVenueStat = React.memo(function OwnerVenueStat({
  icon,
  label,
  value,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  value: number;
}) {
  return (
    <View
      className="min-w-[100px] flex-1 rounded-[18px] border px-4 py-3"
      style={{
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surface,
      }}>
      <View className="flex-row items-center gap-2">
        <Icon as={icon} size={14} className="text-primary" />
        <Text className="text-lg font-extrabold text-foreground">{value}</Text>
      </View>
      <Text variant="small" className="mt-1 text-muted-foreground">
        {label}
      </Text>
    </View>
  );
});

const DesktopVenueEventRow = React.memo(function DesktopVenueEventRow({
  event,
  onOpenEvent,
}: {
  event: DiscoveryEvent;
  onOpenEvent: (eventID: string) => void;
}) {
  const handlePress = React.useCallback(() => {
    onOpenEvent(event.id);
  }, [event.id, onOpenEvent]);

  return (
    <Pressable
      className="flex-row items-center gap-5 border-b border-border py-5"
      onPress={handlePress}>
      <View className="w-[92px] shrink-0 gap-1">
        <Text variant="small" className="uppercase tracking-[1.6px] text-primary">
          {formatEventDayLabel(event.starts_at)}
        </Text>
        <Text variant="small" className="text-muted-foreground">
          {formatEventTime(event.starts_at)}
        </Text>
      </View>
      <View className="min-w-0 flex-1 gap-1">
        <Text className="text-[24px] font-semibold leading-8 text-foreground" numberOfLines={2}>
          {event.title}
        </Text>
        <Text variant="small" className="text-muted-foreground">
          Tap through for the full event page and details.
        </Text>
      </View>
      <View className="flex-row items-center gap-2">
        <Text variant="small" className="font-semibold text-primary">
          View details
        </Text>
        <Icon as={ArrowRightIcon} size={16} className="text-primary" />
      </View>
    </Pressable>
  );
});

const VenueOwnerEventCard = React.memo(function VenueOwnerEventCard({
  event,
  mode,
  onOpenEvent,
  onEditEvent,
  onDeleteEvent,
}: {
  event: VenueOwnerEventSummary;
  mode: 'upcoming' | 'past';
  onOpenEvent?: (eventID: string) => void;
  onEditEvent?: (eventID: string) => void;
  onDeleteEvent?: (event: VenueOwnerEventSummary) => void;
}) {
  const handleOpen = React.useCallback(() => {
    onOpenEvent?.(event.id);
  }, [event.id, onOpenEvent]);
  const handleEdit = React.useCallback(() => {
    onEditEvent?.(event.id);
  }, [event.id, onEditEvent]);
  const handleDelete = React.useCallback(() => {
    onDeleteEvent?.(event);
  }, [event, onDeleteEvent]);

  return (
    <View className="rounded-[24px] border border-border bg-surface-alt p-4">
      <View className="flex-row items-start gap-3">
        <Pressable className="flex-1 gap-2" onPress={handleOpen}>
          <Text className="text-lg font-semibold text-foreground">{event.title}</Text>
          <Text variant="muted" className="leading-6">
            {formatEventDayLabel(event.starts_at)} · {formatEventTime(event.starts_at)}
          </Text>
          <View className="flex-row flex-wrap items-center gap-4 pt-1">
            <View className="flex-row items-center gap-1.5">
              <Icon as={EyeIcon} size={14} className="text-muted-foreground" />
              <Text variant="small" className="text-muted-foreground">
                {event.views} views
              </Text>
            </View>
            <View className="flex-row items-center gap-1.5">
              <Icon as={CheckIcon} size={14} className="text-muted-foreground" />
              <Text variant="small" className="text-muted-foreground">
                {event.rsvps} going
              </Text>
            </View>
          </View>
          {event.rsvps > 0 ? (
            <Text variant="small" className="text-muted-foreground">
              Open event to see who&apos;s going.
            </Text>
          ) : null}
        </Pressable>
        <View className="gap-2">
          <Button variant="secondary" size="sm" onPress={handleOpen}>
            <Icon as={EyeIcon} size={14} className="text-foreground" />
            <Text>View</Text>
          </Button>
          {mode === 'upcoming' ? (
            <View className="gap-2">
              <Button variant="outline" size="sm" onPress={handleEdit}>
                <Icon as={PencilIcon} size={14} className="text-foreground" />
                <Text>Edit</Text>
              </Button>
              <Button variant="destructive" size="sm" onPress={handleDelete}>
                <Icon as={Trash2Icon} size={14} className="text-white" />
                <Text>Delete</Text>
              </Button>
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
});

function VenueDealsSection({
  deals,
  isSavingDeal,
  onDirections,
  onSave,
  onShare,
}: {
  deals: VenuePublicDeal[];
  isSavingDeal?: boolean;
  onDirections?: (deal: VenuePublicDeal) => void;
  onSave?: (deal: VenuePublicDeal) => void;
  onShare?: (deal: VenuePublicDeal) => void;
}) {
  if (deals.length === 0) return null;

  const liveCount = deals.filter((deal) => getDealTiming(deal).state === 'live').length;

  return (
    <View className="gap-3">
      <View className="flex-row items-center justify-between gap-3">
        <Text className="text-xl font-semibold text-foreground">Happy hours & specials</Text>
        <Text
          variant="small"
          className={liveCount > 0 ? 'font-semibold text-orange-500' : 'text-muted-foreground'}>
          {liveCount > 0
            ? `${liveCount} live now`
            : `${deals.length} ${deals.length === 1 ? 'deal' : 'deals'}`}
        </Text>
      </View>
      <View className="flex-row flex-wrap gap-3">
        {deals.map((deal) => (
          <View key={deal.id} className="min-w-[260px] flex-1">
            <VenueDealCard
              deal={deal}
              isSaving={isSavingDeal}
              onDirections={onDirections ? () => onDirections(deal) : undefined}
              onSave={onSave ? () => onSave(deal) : undefined}
              onShare={onShare ? () => onShare(deal) : undefined}
            />
          </View>
        ))}
      </View>
    </View>
  );
}
